import { Component, lazy } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { Wrapper } from 'ui/sidebar';
import MobileRoutes from 'components/MobileRoutes';
import Conversations from './Conversations';
import {
   allowedToAccessConversations,
   allowedToAccessForum,
   allowedToAccessReports,
   allowedToAccessSupport,
} from 'utils/roles';
import { selectCurrentRole } from 'selectors';
import { SERGIC_OFFER } from '../../constants';
import queryString from 'query-string';
import { getApiQueryFromPaginationQuery } from 'utils/routes';
import { selectPlaceReports, selectPlaceReportsPagination } from 'selectors/reports';
import { getPlaceReports } from 'actions/reports';
import { CardFooter, CardFooterLink, CardHeaderMeta, CardTitle } from 'ui';
import SectionTitle from 'components/SectionTitle';
import { selectTopics } from 'selectors/forum';
import { getTopicCategories, getTopics } from 'actions/forum';
import snakeCase from 'lodash/snakeCase';
import { getPlaceEquipments } from 'actions/places';
import { withTranslation } from 'react-i18next';
import { Box } from '@tymate/margaret';
import capitalize from 'lodash/capitalize';

const Reports = lazy(() => import('./Reports'));
const Forum = lazy(() => import('./Forum'));
const Contacts = lazy(() => import('./Contacts'));

class Communication extends Component {
   state = {};

   componentDidMount() {
      this.fetchData();
   }

   fetchData = async (page = 1) => {
      const {
         selectedPlaceId,
         sort,
         getPlaceEquipments,
         getPlaceReports,
         getTopicCategories,
         getTopics,
         topicsSort,
         role,
      } = this.props;

      getTopicCategories(selectedPlaceId);
      getPlaceEquipments(selectedPlaceId);

      const search = queryString.parse(
         getApiQueryFromPaginationQuery(
            queryString.stringify({
               by_state: 'opened,acknowledged',
               ...{ sort },
               page,
            })
         )
      );

      this.setState({
         isFetching: true,
      });

      if (allowedToAccessReports.indexOf(role) > -1) {
         await getPlaceReports(selectedPlaceId, queryString.stringify(search));
      }

      if (role !== 'coownership_board_member') {
         return;
      }

      await getTopics(
         selectedPlaceId,
         queryString.stringify({
            by_state: 'active',
            sort: topicsSort.indexOf('-') === 0 ? `-${snakeCase(topicsSort)}` : snakeCase(topicsSort),
         })
      );

      this.setState({
         isFetching: false,
      });
   };

   render() {
      const { match, role, reports, reportsPagination, topics, isImpersonating, t } = this.props;
      const { path } = match;

      const reportText = reportsPagination.total === 1 ? t('reports:report') : t('reports:reports');
      const reportTitle = reportsPagination.total
         ? `${reportsPagination.total} ${reportText}`.trim()
         : capitalize(reportText);

      return (
         <Wrapper>
            <Switch>
               {!isImpersonating && (
                  <Route path={`${path}/mes-messages/:messageId?/(details)?/:memberId?`} component={Conversations} />
               )}

               <Route>
                  <Switch>
                     <Route path={`${path}/signalements`} component={Reports} />

                     {/* <Route path={`${path}/support/:ticketId?`} component={Support} /> */}

                     {allowedToAccessForum.indexOf(role) > -1 && <Route path={`${path}/forum`} component={Forum} />}

                     <Route path={`${path}/contacts/:contactId?`} component={Contacts} />

                     <Route
                        render={() => (
                           <MobileRoutes
                              redirectionURL={
                                 allowedToAccessConversations.indexOf(role) > -1 && !isImpersonating
                                    ? `${path}/mes-messages`
                                    : `${path}/contacts`
                              }
                              role={role}
                              links={[
                                 {
                                    url: `${path}/support`,
                                    label: t('communications:support'),
                                    allowedFor: allowedToAccessSupport,
                                 },
                                 {
                                    url: `${path}/mes-messages`,
                                    label: t('communications:messages'),
                                    allowedFor: isImpersonating ? [] : allowedToAccessConversations,
                                 },
                                 {
                                    url: `${path}/forum`,
                                    label:
                                       SERGIC_OFFER === 'DIR'
                                          ? t('communications:buildingForum')
                                          : t('communications:syndicForum'),
                                    allowedFor: allowedToAccessForum,
                                 },
                                 {
                                    url: `${path}/signalements`,
                                    label: t('communications:reports'),
                                    allowedFor: allowedToAccessReports,
                                 },
                                 {
                                    url: `${path}/contacts`,
                                    label: t('communications:contacts'),
                                 },
                              ]}
                              top={
                                 SERGIC_OFFER === 'DIR' && (
                                    <div style={{ marginBottom: 16 }}>{t('communications:restriction')}</div>
                                 )
                              }
                           >
                              <>
                                 {allowedToAccessReports.indexOf(role) > -1 && reports.length > 0 && (
                                    <Box paddingTop={2} paddingBottom={2}>
                                       <SectionTitle
                                          title={reportTitle}
                                          creationLink='/nouveau-signalement'
                                          readOnly={isImpersonating}
                                       />

                                       <CardFooter variant='bareBottom'>
                                          <CardFooterLink to='/mes-echanges/signalements'>
                                             {t('reports:allReports')}
                                          </CardFooterLink>
                                       </CardFooter>
                                    </Box>
                                 )}

                                 {allowedToAccessForum.indexOf(role) > -1 && topics.length > 0 && (
                                    <Box paddingTop={1} paddingBottom={2}>
                                       <CardHeaderMeta>
                                          <CardTitle>{t('forum:subjects')}</CardTitle>
                                       </CardHeaderMeta>

                                       <CardFooter variant='bareBottom'>
                                          <CardFooterLink to='/mes-echanges/forum'>
                                             {t('forum:allTopics')}
                                          </CardFooterLink>
                                       </CardFooter>
                                    </Box>
                                 )}
                              </>
                           </MobileRoutes>
                        )}
                     />
                  </Switch>
               </Route>
            </Switch>
         </Wrapper>
      );
   }
}

const mapStateToProps = (state, props) => ({
   selectedPlaceId: state.ui.selectedPlaceId,
   role: selectCurrentRole(state),
   reports: selectPlaceReports(state, props),
   reportsPagination: selectPlaceReportsPagination(state, props),
   sort: state.ui.reportsSorting,
   topicsSort: state.ui.topicsSort,
   topics: selectTopics(state, props),
   isImpersonating: Boolean(state.user.impersonatedUser),
});

const mapDispatchToProps = dispatch => ({
   getPlaceEquipments: placeId => dispatch(getPlaceEquipments(placeId)),
   getPlaceReports: (...args) => dispatch(getPlaceReports(...args)),
   getTopics: (...args) => dispatch(getTopics(...args)),
   getTopicCategories: (...args) => dispatch(getTopicCategories(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Communication));
